import * as React from "react";
import { AlertTriangle, CheckCircle2, Circle, Clock, Plug } from "lucide-react";

import { Badge, cn } from "@ando/ui";
import type { AdminConnectorLicense } from "@ando/core";

/**
 * ConnectorMatrix — status overview grid for the org's licensed
 * connectors.
 *
 * v1 reference: ``ando-frontend/app/admin-v2/components/pages/
 * organizations/components/ConnectorMatrix.tsx`` (209 LoC member×
 * connector grid). The v2 per-member grid already lives inline in
 * ``OrgConnectorsTab``; this header-level matrix shows the lifecycle
 * status (Connesso / Errore / Mai testato / Scaduto) at-a-glance so
 * the admin can spot trouble before drilling into the per-row toggles.
 *
 * Rows = enabled connectors, cols = lifecycle states. Each cell is a
 * chip-pill with the count; clicking a non-zero cell calls
 * ``onCellClick(connectorKey, state)`` so the parent can filter the
 * member grid below.
 *
 * BE gap (May 2026): the BE does NOT yet expose per-connector
 * lifecycle stats — there's only ``GET /platform-admin/organizations/
 * {id}/connectors`` which returns the license row, not OAuth status.
 * Counts default to zero with a hint banner. The cell structure is
 * future-proof: when ``GET /platform-admin/organizations/{id}/
 * connectors/status`` ships, the parent only needs to pass the new
 * ``stats`` prop.
 */

export type ConnectorState = "connected" | "error" | "untested" | "expired";

const STATE_DEFS: ReadonlyArray<{
  id: ConnectorState;
  label: string;
  icon: typeof CheckCircle2;
  tone: string;
  chipTone: string;
}> = [
  {
    id: "connected",
    label: "Connesso",
    icon: CheckCircle2,
    tone: "text-success-foreground",
    chipTone: "bg-success/15 text-success-foreground",
  },
  {
    id: "error",
    label: "Errore",
    icon: AlertTriangle,
    tone: "text-destructive",
    chipTone: "bg-destructive/15 text-destructive",
  },
  {
    id: "expired",
    label: "OAuth scaduto",
    icon: Clock,
    tone: "text-warning-foreground",
    chipTone: "bg-warning/15 text-warning-foreground",
  },
  {
    id: "untested",
    label: "Mai testato",
    icon: Circle,
    tone: "text-ink-3",
    chipTone: "bg-cream-deep text-ink-2",
  },
];

export interface ConnectorMatrixStats {
  /** ``{ [connectorKey]: { [state]: count } }`` — sparse OK. */
  byConnector: Record<string, Partial<Record<ConnectorState, number>>>;
}

export interface ConnectorMatrixProps {
  licenses: AdminConnectorLicense[];
  /** Optional pre-computed stats from BE; defaults to all-zero. */
  stats?: ConnectorMatrixStats;
  /**
   * Optional display-name resolver — when the catalog isn't yet wired
   * we fall back to the connector_key. Keeps the matrix decoupled from
   * the catalog endpoint.
   */
  displayName?: (connectorKey: string) => string;
  /** Drill-down — clicking a non-zero cell. */
  onCellClick?: (connectorKey: string, state: ConnectorState) => void;
}

export function ConnectorMatrix({
  licenses,
  stats,
  displayName,
  onCellClick,
}: ConnectorMatrixProps): React.ReactElement {
  const enabled = React.useMemo(
    () => licenses.filter((l) => l.is_enabled),
    [licenses],
  );

  const totals = React.useMemo<Record<ConnectorState, number>>(() => {
    const acc: Record<ConnectorState, number> = {
      connected: 0,
      error: 0,
      expired: 0,
      untested: 0,
    };
    if (!stats) return acc;
    for (const key of Object.keys(stats.byConnector)) {
      const row = stats.byConnector[key];
      for (const s of Object.keys(acc) as ConnectorState[]) {
        acc[s] += row?.[s] ?? 0;
      }
    }
    return acc;
  }, [stats]);

  if (enabled.length === 0) {
    return (
      <section
        aria-labelledby="connector-matrix-empty"
        className="flex flex-col items-center justify-center gap-2 rounded-lg border border-hairline border-dashed bg-card/40 px-6 py-8 text-ink-3"
      >
        <Plug className="h-6 w-6 opacity-40" aria-hidden />
        <p id="connector-matrix-empty" className="font-medium text-[12.5px]">
          Nessun connector licenziato
        </p>
        <p className="text-[11.5px]">
          Abilita un connector qui sotto per popolare la matrice di stato.
        </p>
      </section>
    );
  }

  const resolveLabel = (key: string): string =>
    displayName ? displayName(key) : key;

  return (
    <section
      aria-labelledby="connector-matrix-heading"
      className="rounded-lg border border-hairline bg-card/55 p-4"
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <h3
            id="connector-matrix-heading"
            className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
          >
            Stato dei connector
          </h3>
          <p className="mt-1 text-[11.5px] text-ink-3">
            {enabled.length} connector attivi · click su una cella per filtrare
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {STATE_DEFS.map((s) => {
            const Icon = s.icon;
            return (
              <Badge
                key={s.id}
                variant="outline"
                className="inline-flex items-center gap-1 text-[10.5px]"
              >
                <Icon size={10} aria-hidden className={s.tone} />
                {s.label}: <span className="tabular-nums">{totals[s.id]}</span>
              </Badge>
            );
          })}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-hairline border-b">
              <th
                scope="col"
                className="sticky left-0 z-10 bg-card/55 px-3 py-2 text-left font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                Connector
              </th>
              {STATE_DEFS.map((s) => (
                <th
                  key={s.id}
                  scope="col"
                  className="px-3 py-2 text-center font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
                >
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {enabled.map((l) => {
              const row = stats?.byConnector[l.connector_key] ?? {};
              return (
                <tr
                  key={l.connector_key}
                  className="border-hairline/60 border-b last:border-b-0"
                >
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-card/55 px-3 py-2 text-left font-medium text-ink-1"
                  >
                    <div className="flex flex-col">
                      <span className="truncate">
                        {resolveLabel(l.connector_key)}
                      </span>
                      <span className="font-mono text-[10.5px] text-ink-3">
                        {l.assignment_mode === "all_members"
                          ? "tutti i membri"
                          : "per utente"}
                      </span>
                    </div>
                  </th>
                  {STATE_DEFS.map((s) => {
                    const count = row[s.id] ?? 0;
                    const clickable = count > 0 && !!onCellClick;
                    return (
                      <td
                        key={s.id}
                        className="px-3 py-2 text-center"
                      >
                        <button
                          type="button"
                          disabled={!clickable}
                          onClick={() =>
                            onCellClick?.(l.connector_key, s.id)
                          }
                          className={cn(
                            "inline-flex min-w-[36px] items-center justify-center gap-1 rounded-sm px-2 py-0.5 font-medium text-[11px] tabular-nums transition-colors",
                            count > 0
                              ? s.chipTone
                              : "text-ink-3",
                            clickable
                              ? "cursor-pointer hover:ring-2 hover:ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                              : "cursor-default",
                          )}
                          aria-label={`${resolveLabel(l.connector_key)} · ${s.label}: ${count}`}
                        >
                          {count}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
